import { type FC, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Bot, Brush, Check, MapPin, UserPlus } from 'lucide-react';
import { ButtonLink } from '@/components/Button/ButtonLink';
import { PageHero } from '@/components/PageHero/PageHero';
import { usePlans } from '@/hooks/usePlans';
import { LandingPricing } from './LandingPricing';
import { ShotPair, type ShotName } from './LandingShot';

interface LandingProps {
  /** The public events pages are switched on, so the landing page can link to them. */
  publicEvents: boolean;
}

const STEPS = [
  { key: 'collection', icon: Brush, title: 'Collection and painting', text: 'Add your models, then tick them off from box to battle-ready. Photograph progress as you go.' },
  { key: 'games', icon: Bot, title: 'Games and Grimm', text: 'Log your results in seconds and ask Grimm rules questions. Answers point to their sources.' },
  { key: 'clubs', icon: MapPin, title: 'Clubs and events', text: 'Find clubs, stores and gaming nights near you, and see what is on this fortnight.' },
] as const;

interface FeatureProps {
  id: string;
  title: string;
  intro: string;
  points: string[];
  desktop: { name: ShotName; alt: string };
  phone: { name: ShotName; alt: string };
  /** Alternate the side the pictures sit on, so the page has some rhythm. */
  flip?: boolean;
  children?: ReactNode;
}

const Feature: FC<FeatureProps> = ({ id, title, intro, points, desktop, phone, flip, children }) => (
  <section aria-labelledby={`${id}-heading`} className="py-12">
    <div className="container-app grid max-w-6xl items-center gap-10 lg:grid-cols-2 lg:gap-16">
      <div className={flip ? 'lg:order-2' : undefined}>
        <h2 id={`${id}-heading`} className="font-display text-3xl font-bold text-accent-primary">{title}</h2>
        <p className="mt-3 text-lg text-text-secondary">{intro}</p>
        <ul className="mt-5 space-y-3">
          {points.map((point) => (
            <li key={point} className="flex items-start gap-3 text-text-primary">
              <Check aria-hidden="true" size={18} className="mt-1 shrink-0 text-accent-primary" />
              <span>{point}</span>
            </li>
          ))}
        </ul>
        {children}
      </div>
      <div className={flip ? 'lg:order-1' : undefined}>
        <ShotPair desktop={desktop} phone={phone} phoneLeft={flip} />
      </div>
    </div>
  </section>
);

/**
 * Landing - the page signed-out visitors see at the front door: what the app does, shown with real screenshots,
 * what it costs, and a way in. No testimonials or user numbers: there is nothing to claim yet.
 */
export const Landing: FC<LandingProps> = ({ publicEvents }) => {
  const plans = usePlans();
  return (
    <div>
      <PageHero heroKey="home" priority>
        <div className="grid items-center gap-8 py-10 lg:grid-cols-[1fr_1.1fr] lg:py-14">
          <div>
            <p className="font-display text-sm font-semibold uppercase tracking-widest text-text-secondary page-hero__text">Grim Dark Companion</p>
            <h1 className="page-hero__text mt-2 font-display text-4xl font-bold text-accent-primary md:text-5xl">From box to battle-ready.</h1>
            <p className="page-hero__text mt-4 max-w-xl text-lg text-text-primary">
              The companion for your whole tabletop hobby. Track every model you paint, ask Grimm for rules answers with sources, log your games and find a club near you.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <ButtonLink to="/register" variant="primary" size="lg">
                <UserPlus aria-hidden="true" size={18} />
                Create account
              </ButtonLink>
              {publicEvents && (
                <ButtonLink to="/events" variant="secondary" size="lg">
                  <MapPin aria-hidden="true" size={18} />
                  Find local events
                </ButtonLink>
              )}
            </div>
            <p className="page-hero__text mt-3 text-sm text-text-secondary">
              Free to start{plans.prices.welcomeWeekDays > 0 ? `, with a ${plans.prices.welcomeWeekDays}-day Premium welcome week. No card needed.` : '.'}{' '}
              <Link to="/login" className="text-accent-primary underline">Already have an account? Sign in</Link>
            </p>
          </div>
          <ShotPair
            eager
            desktop={{ name: 'hobby', alt: 'The Hobby overview: models owned, painted and still in the Pile of Potential, with a suggestion for what to paint next.' }}
            phone={{ name: 'home', alt: 'The Home screen on a phone, suggesting the next painting project to pick up.' }}
          />
        </div>
      </PageHero>

      <section aria-labelledby="how-heading" className="py-12">
        <div className="container-app max-w-6xl">
          <h2 id="how-heading" className="text-center font-display text-3xl font-bold text-accent-primary">How it works</h2>
          <ol className="mt-8 grid gap-4 md:grid-cols-3">
            {STEPS.map(({ key, icon: Icon, title, text }, i) => (
              <li key={key} className="rounded-lg border border-border-subtle bg-bg-secondary p-5">
                <span className="flex items-center gap-3">
                  <span aria-hidden="true" className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-primary/10 text-accent-primary"><Icon size={20} /></span>
                  <span className="text-sm text-text-secondary">Step {i + 1}</span>
                </span>
                <h3 className="mt-3 text-lg font-semibold text-text-primary">{title}</h3>
                <p className="mt-1 text-text-secondary">{text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <Feature
        id="collection"
        title="See every model move from box to battle-ready"
        intro="Your collection, your Pile of Potential and your painting progress, in one place that is quick to update with a thumb."
        points={[
          'Track progress per model: five of ten painted is five of ten, not a vague "painting".',
          'Your Pile of Potential shows what is waiting, without the guilt.',
          'Compare then and now on any unit, with a progress timeline built from your photos.',
        ]}
        desktop={{ name: 'collection', alt: 'A collection grid showing squads with progress bars, such as 12 of 20 painted, and photos from the painting desk.' }}
        phone={{ name: 'unit', alt: 'A unit page showing a then-and-now pair of photos of a hooded warband and its status history.' }}
      />

      <Feature
        id="journal"
        flip
        title="A painting journal and projects you can finish"
        intro="Photograph your work, tag the models in the shot, and plan projects with a date to aim for."
        points={[
          'Journal photos sit beside the models they show, and you can add a few seconds after each session.',
          'Projects link to your models, with a gentle pace: how many models a week to hit your date.',
          'Terrain, conversions and whole armies all fit.',
        ]}
        desktop={{ name: 'journal', alt: 'The painting journal: a grid of photos of painted miniatures, terrain and a display cabinet.' }}
        phone={{ name: 'project', alt: 'A project page for an army with sixteen of forty-two models painted and about seven models a week to finish on time.' }}
      />

      <Feature
        id="paint-next"
        title="Never wonder what to paint next"
        intro="Paint next picks a project or unit for your next brush session, from what you have started and what is due."
        points={[
          'Suggestions explain why, so they feel like advice, not nagging.',
          'Hide a suggestion for a week or a month, and it comes back by itself.',
          'Pin it to Home so the answer is waiting when you sit down.',
        ]}
        desktop={{ name: 'paint-next', alt: 'The Paint next card suggesting a skirmish band to pick up where you left off.' }}
        phone={{ name: 'paint-next', alt: 'Paint next on a phone, with a project suggestion and options to hide it for a while.' }}
      />

      <Feature
        id="grimm"
        flip
        title="Ask Grimm, and see where the answer came from"
        intro="Grimm is your rules archivist. Ask in plain words and get an answer that points to its sources, so you can check it at the table."
        points={[
          'Searching the rules library is free and unlimited.',
          'Answers show their sources, and Grimm says when it is not sure.',
          'Grimm can use your games and collection, and you choose whether it does.',
        ]}
        desktop={{ name: 'grimm', alt: 'Grimm answering a question about what to check before a game, with its sources listed underneath.' }}
        phone={{ name: 'grimm', alt: 'Grimm on a phone giving a short checklist for setting up a game, with a link to show sources.' }}
      />

      <Feature
        id="games"
        title="Log a game in seconds, and look back at your record"
        intro="Record who you played, the score and a note, then watch your wins, draws and losses build up over time."
        points={[
          'Log a finished game in a few seconds, or track one live with a friend.',
          'Your history stays with you, and Grimm can draw on it if you let it.',
          'Every game counts, including the narrow losses.',
        ]}
        desktop={{ name: 'results', alt: 'Match history with a chart of wins, draws and losses by month, and a list of recent games.' }}
        phone={{ name: 'log-result', alt: 'The Log a result form on a phone, filled in with a game, an opponent and a score.' }}
      />

      <Feature
        id="clubs"
        flip
        title="Find your club, and what is on near you"
        intro="Clubs, stores and regular gaming nights on a map. Anyone can look, and anyone can add their own club."
        points={[
          'See clubs and stores near your town, with their next events.',
          'Tell a club you play there, and they only see how many regulars there are, never who.',
          'Public events pages and calendar links work without an account.',
        ]}
        desktop={{ name: 'near-me', alt: 'Near me: a list of nearby stores and clubs beside a map of Hampshire.' }}
        phone={{ name: 'club', alt: 'A club page listing upcoming events and a map of where it meets.' }}
      >
        {publicEvents && (
          <p className="mt-5">
            <Link to="/events" className="font-medium text-accent-primary underline">Browse local events without an account</Link>
          </p>
        )}
      </Feature>

      <LandingPricing plans={plans} />

      <section aria-labelledby="final-heading" className="py-14 text-center">
        <div className="container-app max-w-3xl">
          <h2 id="final-heading" className="font-display text-3xl font-bold text-accent-primary">Ready for your next session?</h2>
          <p className="mt-3 text-text-secondary">Create a free account in a minute and start with the models on your desk.</p>
          <div className="mt-6 flex justify-center">
            <ButtonLink to="/register" variant="primary" size="lg">
              <UserPlus aria-hidden="true" size={18} />
              Create account
            </ButtonLink>
          </div>
          <p className="mt-4 text-sm text-text-muted">
            Not ready to sign up?{' '}
            <Link to="/waitlist" className="text-accent-primary underline">Get news and updates by email</Link>. It is a mailing list, not an account.
          </p>
          <p className="mt-2 text-xs text-text-muted">
            By creating an account you agree to our{' '}
            <Link to="/terms-and-conditions" className="text-accent-primary underline">Terms of Service</Link> and{' '}
            <Link to="/privacy-policy" className="text-accent-primary underline">Privacy Policy</Link>.
          </p>
          <p className="mt-4 text-xs text-text-muted">
            Works alongside Warhammer 40,000, Age of Sigmar, Kill Team, Blood Bowl and more. An independent fan project: all game names and trademarks belong to their owners.
          </p>
        </div>
      </section>
    </div>
  );
};

